import { Link } from 'react-router-dom';
import { ReactNode } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import JsonLd, { generateFaqSchema } from '@/components/seo/JsonLd';

interface FAQItem {
  question: string;
  answer: string | ReactNode;
}

interface FAQSectionProps {
  title?: string;
  subtitle?: string;
  faqs?: FAQItem[];
  customFaqs?: FAQItem[];
  showWhatsAppCTA?: boolean;
  showTitle?: boolean;
}

// Plain text versions for schema (without JSX)
const defaultFaqSchemaData = [
  {
    question: 'What Apple devices do you repair in Patna?',
    answer: 'We repair all Apple devices including iPhone (all models from iPhone 6 to iPhone 16), MacBook (Air, Pro, all generations), iPad (all models), and Apple Watch (Series 3 to Ultra 2). Our expert technicians are trained to handle all types of repairs.',
  },
  {
    question: 'How long does iPhone screen repair take?',
    answer: 'Most iPhone screen repairs are completed within 2-4 hours. We offer same-day service for screen and battery replacements. For complex repairs like logic board issues, it may take 24-48 hours.',
  },
  {
    question: 'Do you use genuine Apple parts for repairs?',
    answer: 'Yes, we use only high-quality, genuine parts for all our repairs. This ensures your device performs like new after the repair. All our parts come with a warranty, and we never compromise on quality.',
  },
  {
    question: 'What is the warranty on repairs?',
    answer: 'All repairs at iFix Apple Care come with a 6-month warranty. This covers any defects in the replacement parts or workmanship. If you face any issues with the repaired part within the warranty period, we will fix it free of charge.',
  },
  {
    question: 'What are your service center timings and location?',
    answer: 'Our service center is located at Shop No. 6, Jassinandan Plaza, New Dak Bunglow Road, Patna, Bihar 800001. We are open Monday to Saturday from 10:00 AM to 8:00 PM, and Sunday from 11:00 AM to 5:00 PM.',
  },
  {
    question: 'Do you offer pickup and delivery service in Patna?',
    answer: 'Yes, we offer free pickup and delivery service within Patna for major repairs. For customers outside Patna, we accept devices via courier. Contact us on WhatsApp or call us to arrange pickup.',
  },
  {
    question: 'Can you repair water-damaged iPhones and MacBooks?',
    answer: 'Yes, we specialize in water damage repair for all Apple devices. Our technicians use advanced techniques to clean and restore water-damaged iPhones and MacBooks. The success rate depends on the extent of damage and how quickly you bring the device to us.',
  },
  {
    question: 'How much does iPhone battery replacement cost in Patna?',
    answer: 'iPhone battery replacement costs vary by model. Contact us for the exact price for your specific iPhone model. We use genuine batteries and the replacement includes a 6-month warranty.',
  },
  {
    question: 'Do you offer franchise opportunities?',
    answer: 'Yes! We offer franchise opportunities across Bihar and neighboring states. Join the iFix Apple Care family and start your own Apple service center with our training, support, and brand recognition.',
  },
  {
    question: 'Can I track my repair status?',
    answer: 'Yes, once you drop off your device, we provide you with a repair ticket number. You can call us or message on WhatsApp to check the status of your repair anytime. We also send updates when your device is ready for pickup.',
  },
];

const defaultFaqs: FAQItem[] = [
  {
    question: 'What Apple devices do you repair in Patna?',
    answer: (
      <>
        We repair all Apple devices including <Link to="/iphone-service" className="text-primary hover:underline">iPhone</Link> (all models from iPhone 6 to iPhone 16), 
        <Link to="/macbook-service" className="text-primary hover:underline"> MacBook</Link> (Air, Pro, all generations), 
        <Link to="/ipad-service" className="text-primary hover:underline"> iPad</Link> (all models), and 
        <Link to="/watch-service" className="text-primary hover:underline"> Apple Watch</Link> (Series 3 to Ultra 2). 
        Our expert technicians are trained to handle all types of repairs.
      </>
    ),
  },
  {
    question: 'How long does iPhone screen repair take?',
    answer: (
      <>
        Most <Link to="/iphone-service" className="text-primary hover:underline">iPhone screen repairs</Link> are completed within 2-4 hours. 
        We offer same-day service for screen and battery replacements. For complex repairs like logic board issues, 
        it may take 24-48 hours. You can wait at our Patna service center or we will notify you when it is ready.
      </>
    ),
  },
  {
    question: 'Do you use genuine Apple parts for repairs?',
    answer: 'Yes, we use only high-quality, genuine parts for all our repairs. This ensures your device performs like new after the repair. All our parts come with a warranty, and we never compromise on quality.',
  },
  {
    question: 'What is the warranty on repairs?',
    answer: 'All repairs at iFix Apple Care come with a 6-month warranty. This covers any defects in the replacement parts or workmanship. If you face any issues with the repaired part within the warranty period, we will fix it free of charge.',
  },
  {
    question: 'What are your service center timings and location?',
    answer: (
      <>
        Our <Link to="/#contact" className="text-primary hover:underline">service center</Link> is located at Shop No. 6, Jassinandan Plaza, New Dak Bunglow Road, Patna, Bihar 800001. 
        We are open Monday to Saturday from 10:00 AM to 8:00 PM, and Sunday from 11:00 AM to 5:00 PM. 
        You can walk in for a free diagnosis or book an appointment for priority service.
      </>
    ),
  },
  {
    question: 'Do you offer pickup and delivery service in Patna?',
    answer: 'Yes, we offer free pickup and delivery service within Patna for major repairs. For customers outside Patna, we accept devices via courier. Contact us on WhatsApp or call us to arrange pickup.',
  },
  {
    question: 'Can you repair water-damaged iPhones and MacBooks?',
    answer: (
      <>
        Yes, we specialize in water damage repair for all Apple devices. Our technicians use advanced techniques to clean and restore 
        water-damaged <Link to="/iphone-service" className="text-primary hover:underline">iPhones</Link> and 
        <Link to="/macbook-service" className="text-primary hover:underline"> MacBooks</Link>. The success rate depends on 
        the extent of damage and how quickly you bring the device to us. Do not try to charge a water-damaged device.
      </>
    ),
  },
  {
    question: 'How much does iPhone battery replacement cost in Patna?',
    answer: (
      <>
        iPhone battery replacement costs vary by model. Contact us for the exact price for your specific iPhone model. 
        We use genuine batteries and the replacement includes a 6-month warranty. 
        Visit our <Link to="/iphone-service" className="text-primary hover:underline">iPhone service page</Link> for more details.
      </>
    ),
  },
  {
    question: 'Do you offer franchise opportunities?',
    answer: (
      <>
        Yes! We offer <Link to="/franchise" className="text-primary hover:underline">franchise opportunities</Link> across Bihar and neighboring states. 
        Join the iFix Apple Care family and start your own Apple service center with our training, support, and brand recognition.
      </>
    ),
  },
  {
    question: 'Can I track my repair status?',
    answer: 'Yes, once you drop off your device, we provide you with a repair ticket number. You can call us or message on WhatsApp to check the status of your repair anytime. We also send updates when your device is ready for pickup.',
  },
];

const FAQSection = ({ 
  title = 'Frequently Asked Questions',
  subtitle = 'Got questions about Apple device repair in Patna? Find answers to common queries below, or contact us for personalized assistance.',
  faqs,
  customFaqs,
  showWhatsAppCTA = true,
  showTitle = true,
}: FAQSectionProps) => {
  const displayFaqs = customFaqs || faqs || defaultFaqs;
  const schemaFaqs = (customFaqs || faqs)
    ? (customFaqs || faqs || []).map(f => ({ question: f.question, answer: typeof f.answer === 'string' ? f.answer : f.question }))
    : defaultFaqSchemaData;

  return (
    <section className="section-padding bg-secondary" id="faq">
      <JsonLd data={generateFaqSchema(schemaFaqs)} />
      <div className="container-apple">
        {showTitle && (
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4">
              {title}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {subtitle}
            </p>
          </div>
        )}

        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            {displayFaqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-card rounded-2xl border border-border px-6 data-[state=open]:border-primary/30 transition-colors"
              >
                <AccordionTrigger className="text-left text-foreground font-medium py-5 hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {showWhatsAppCTA && (
          <div className="text-center mt-10">
            <p className="text-muted-foreground">
              Still have questions?{' '}
              <a
                href="https://wa.me/919798653591?text=Hi,%20I%20have%20a%20question%20about%20Apple%20device%20repair"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline font-medium"
              >
                Chat with us on WhatsApp
              </a>
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default FAQSection;